import React, { useState } from "react";
import { useAuth } from "utilities/auth";
import { useToast } from "components/Toast";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { Globe, X } from "lucide-react";

/**
 * Add DBA modal — appends a new DBA to an existing application's `dbas[]`.
 *
 * Used from `ApplicationTab`. Fields match the registration schema required
 * set 1:1: `dbaName`, `websiteUrl`, `adminEmail`, `customerServiceEmail` are
 * required; `adminPhone` optional. Server generates `dbaId` and enforces the
 * 3-DBA cap.
 *
 * Partner T&C is gated by the caller — the modal assumes the caller has
 * already confirmed the user can act. As a safety net, a 403
 * PARTNER_TERMS_REQUIRED response bubbles up to the caller via `onTermsRequired`
 * so it can open the T&C modal and retry.
 */
export default function AddDbaModal({
  applicationId,
  onClose,
  onCreated,
  onTermsRequired,
}) {
  const auth = useAuth();
  const effectiveCompanyId = useEffectiveCompanyId();
  const { toast } = useToast();

  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [form, setForm] = useState({
    dbaName: "",
    websiteUrl: "",
    adminEmail: "",
    customerServiceEmail: "",
    adminPhone: "",
  });

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validate = () => {
    const errors = {};
    if (!form.dbaName.trim()) errors.dbaName = "DBA name is required";
    if (!form.websiteUrl.trim()) {
      errors.websiteUrl = "Website URL is required";
    } else if (!/^https?:\/\/.+/i.test(form.websiteUrl.trim())) {
      errors.websiteUrl = "Must be a full URL (https://…)";
    }
    if (!form.adminEmail.trim()) {
      errors.adminEmail = "Admin email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.adminEmail.trim())) {
      errors.adminEmail = "Enter a valid email";
    }
    if (!form.customerServiceEmail.trim()) {
      errors.customerServiceEmail = "Customer service email is required";
    } else if (
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.customerServiceEmail.trim())
    ) {
      errors.customerServiceEmail = "Enter a valid email";
    }
    if (
      form.adminPhone.trim() &&
      !/^[+\d][\d\s\-().]{5,}$/.test(form.adminPhone.trim())
    ) {
      errors.adminPhone = "Enter a valid phone number";
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    const payload = {
      dbaName: form.dbaName.trim(),
      websiteUrl: form.websiteUrl.trim(),
      adminEmail: form.adminEmail.trim(),
      customerServiceEmail: form.customerServiceEmail.trim(),
    };
    if (form.adminPhone.trim()) payload.adminPhone = form.adminPhone.trim();

    setSaving(true);
    try {
      await makeApiRequest(
        "post",
        apiRoutes.appendApplicationDba(applicationId),
        auth.user,
        effectiveCompanyId,
        payload
      );
      toast({
        status: "success",
        title: "DBA added",
        description: `${payload.dbaName} has been added to the application.`,
      });
      onCreated?.();
    } catch (err) {
      const status = err.response?.status;
      const serverError = err.response?.data?.error;

      if (status === 403 && serverError === "PARTNER_TERMS_REQUIRED") {
        // Safety-net: let the caller open T&C modal and retry.
        onTermsRequired?.();
        return;
      }

      console.error("[AddDbaModal] Create failed", err);
      toast({
        status: "destructive",
        title: "Failed to add DBA",
        description: serverError || "Unknown error",
      });
    } finally {
      setSaving(false);
    }
  };

  const inputBase =
    "w-full px-3 py-2 rounded-md bg-card border border-border text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand text-sm";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={saving ? undefined : onClose}
    >
      <div
        className="surface rounded-xl ring-1 ring-border p-6 w-full max-w-lg shadow-lg max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-brand/20 rounded-lg">
              <Globe className="w-5 h-5 text-brand" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">Add DBA</h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Add another DBA to this application.
              </p>
            </div>
          </div>
          <button
            className="btn btn-ghost h-8 w-8 p-0"
            onClick={onClose}
            aria-label="Close"
            disabled={saving}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground block mb-1">
              DBA Name <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              className={`${inputBase} ${
                fieldErrors.dbaName
                  ? "border-destructive ring-destructive"
                  : ""
              }`}
              placeholder="e.g. Acme Supplements"
              value={form.dbaName}
              onChange={(e) => setField("dbaName", e.target.value)}
              autoFocus
            />
            {fieldErrors.dbaName && (
              <p className="text-xs text-destructive mt-1">
                {fieldErrors.dbaName}
              </p>
            )}
          </div>

          <div>
            <label className="text-sm font-medium text-foreground block mb-1">
              Website URL <span className="text-destructive">*</span>
            </label>
            <input
              type="url"
              className={`${inputBase} ${
                fieldErrors.websiteUrl
                  ? "border-destructive ring-destructive"
                  : ""
              }`}
              placeholder="https://www.example.com"
              value={form.websiteUrl}
              onChange={(e) => setField("websiteUrl", e.target.value)}
            />
            {fieldErrors.websiteUrl && (
              <p className="text-xs text-destructive mt-1">
                {fieldErrors.websiteUrl}
              </p>
            )}
          </div>

          <div>
            <label className="text-sm font-medium text-foreground block mb-1">
              Admin Email <span className="text-destructive">*</span>
            </label>
            <input
              type="email"
              className={`${inputBase} ${
                fieldErrors.adminEmail
                  ? "border-destructive ring-destructive"
                  : ""
              }`}
              placeholder="admin@example.com"
              value={form.adminEmail}
              onChange={(e) => setField("adminEmail", e.target.value)}
            />
            {fieldErrors.adminEmail && (
              <p className="text-xs text-destructive mt-1">
                {fieldErrors.adminEmail}
              </p>
            )}
          </div>

          <div>
            <label className="text-sm font-medium text-foreground block mb-1">
              Customer Service Email{" "}
              <span className="text-destructive">*</span>
            </label>
            <input
              type="email"
              className={`${inputBase} ${
                fieldErrors.customerServiceEmail
                  ? "border-destructive ring-destructive"
                  : ""
              }`}
              placeholder="support@example.com"
              value={form.customerServiceEmail}
              onChange={(e) =>
                setField("customerServiceEmail", e.target.value)
              }
            />
            {fieldErrors.customerServiceEmail && (
              <p className="text-xs text-destructive mt-1">
                {fieldErrors.customerServiceEmail}
              </p>
            )}
          </div>

          <div>
            <label className="text-sm font-medium text-foreground block mb-1">
              Admin Phone
            </label>
            <input
              type="tel"
              className={`${inputBase} ${
                fieldErrors.adminPhone
                  ? "border-destructive ring-destructive"
                  : ""
              }`}
              placeholder="Optional"
              value={form.adminPhone}
              onChange={(e) => setField("adminPhone", e.target.value)}
            />
            {fieldErrors.adminPhone && (
              <p className="text-xs text-destructive mt-1">
                {fieldErrors.adminPhone}
              </p>
            )}
          </div>
        </div>

        <div className="shrink-0 flex gap-2 pt-4 mt-4 border-t border-border">
          <button
            className="btn btn-ghost flex-1"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary flex-1"
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? "Adding…" : "Add DBA"}
          </button>
        </div>
      </div>
    </div>
  );
}
